import { ReportTypesEnum } from '../enums/report-types.enum';
import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subscription, take } from 'rxjs';
import { getRiskTypeList } from '../constants/risk-types-list.const';
import { PostMessageTypesEnum } from '../enums/post-message-types.enum';
import { ReportPayload } from '../models/report-payload.model';
import { ToastProps } from '../models/toast.model';
import { TraceabilityData } from '../models/traceability-state.model';
import { UserViewAsSetting } from '../models/user-info-state.model';

interface HsCode {
  HSCode: string;
  Description: string;
}

function isHsCode(value: unknown): value is HsCode {
  if (value === null || typeof value !== 'object') return false;
  const code: { HSCode?: unknown; Description?: unknown } = value;
  return (
    typeof code.HSCode === 'string' && typeof code.Description === 'string'
  );
}

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private readonly datePipe = inject(DatePipe);
  private readonly decimalPipe = inject(DecimalPipe);

  private readonly hsCodesUrl =
    'https://assets.agridence.com/agd-traceability/hscodes/all/v1/codes.json';

  async getHSCodesDropdownOptions(): Promise<
    { label: string; value: string }[]
  > {
    const response = await fetch(this.hsCodesUrl);
    const hsCodes: unknown = await response.json();
    if (!Array.isArray(hsCodes)) throw new Error('Invalid HS code response');
    if (!hsCodes.every(isHsCode)) throw new Error('Invalid HS code entry');

    return hsCodes.map(({ HSCode, Description }) => ({
      label: `${HSCode}: ${Description}`,
      value: HSCode
    }));
  }

  getDateMonth(date: string) {
    return this.datePipe.transform(date, 'MMM yyyy');
  }

  getSiListArray(siList: TraceabilityData[] | null | undefined) {
    if (!siList?.length) {
      return [];
    }
    const siSet = new Set<string>();
    siList.forEach((si) => {
      siSet.add(si.si_number!);
    });
    return Array.from(siSet);
  }

  getSiDetailListArray(siList: TraceabilityData[] | null | undefined) {
    if (!siList?.length) {
      return [];
    }
    const siSet = new Set<number>();
    siList.forEach((si) => {
      siSet.add(si.sidetail_id!);
    });
    return Array.from(siSet);
  }

  constructMultipleEmailsQuery(emails: string[]) {
    let emailListQuery = '';
    // The backend accepts repeated query parameters.
    const queryProp = 'emails';
    emails.forEach((email) => {
      emailListQuery += `&${queryProp}=${email}`;
    });

    return emailListQuery;
  }

  constructMultipleSiQuery(siList: (number | string | undefined)[]) {
    let siListQuery = '';
    // The backend accepts repeated query parameters.
    const queryProp = 'si';
    siList.forEach((si, index) => {
      if (index === 0) {
        siListQuery += `?${queryProp}=${si}`;
      } else {
        siListQuery += `&${queryProp}=${si}`;
      }
    });

    return siListQuery;
  }

  constructMultipleEudrFilenames(filenames: string[]): string {
    let params = new HttpParams();
    filenames.forEach((filename) => {
      params = params.append('eudr_filenames', filename);
    });
    return '&' + params.toString();
  }

  constructMultipleSiDetailQuery(
    siList: (number | string | null | undefined)[]
  ) {
    let siDetailListQuery = '';
    const queryProp = 'sidetail_id';
    siList.forEach((si, index) => {
      if (index === 0) {
        siDetailListQuery += `?${queryProp}=${si}`;
      } else {
        siDetailListQuery += `&${queryProp}=${si}`;
      }
    });

    return siDetailListQuery;
  }

  areArraysEqual<T>(array1: T[], array2: T[]): boolean {
    return (
      array1.length === array2.length &&
      array1.every((value, index) => value === array2[index])
    );
  }

  emitToast(toast: ToastProps) {
    this.emitPropToParent('type', PostMessageTypesEnum.TOAST, 'toast', toast);
  }

  emitPropToParent<T, U>(key1: string, value1: T, key2?: string, value2?: U) {
    const message = {
      ...(key1 && { [key1]: value1 }),
      ...(key2 && { [key2]: value2 })
    };
    window.parent.postMessage(message, '*');
  }

  calculateSum<T extends object>(data: (T | undefined)[], property: keyof T) {
    if (data.every((object) => object !== undefined)) {
      return data.reduce((total: number, object) => {
        const rawValue = object[property];
        const value =
          rawValue === undefined || rawValue === null ? 0 : Number(rawValue);
        return total + value;
      }, 0);
    }

    return 0;
  }

  getFileNameFromHeaders(response: HttpResponse<Blob>) {
    const contentDisposition =
      response.headers.get('content-disposition') || '';
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    let fileName = 'download';
    const matches = filenameRegex.exec(contentDisposition);
    if (matches?.[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }

    return fileName;
  }

  getFileNameFromUrl(url: string): string {
    const filename = url.slice(url.lastIndexOf('/') + 1);
    const queryStart = filename.indexOf('?');
    return queryStart === -1 ? filename : filename.slice(0, queryStart);
  }

  getPlantationCodesArray<K extends string>(
    plantationList: Record<K, string>[],
    plantationCodeProp: K
  ) {
    const plantationCodes = new Set<string>();
    plantationList.forEach((plantation) => {
      plantationCodes.add(plantation[plantationCodeProp]);
    });
    return Array.from(plantationCodes);
  }

  unsubscribeSubs(subs: Subscription[]) {
    subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  retrieveFromObservable<T>(sub: Observable<T> | null | undefined): T | null {
    let values: T | null = null;
    if (sub) {
      sub.pipe(take(1)).subscribe((value) => (values = value));
    }

    return values;
  }

  constructReportPayload<T>(reportType: ReportTypesEnum, data: T) {
    const payload: ReportPayload<typeof data> = {
      report_type: reportType,
      data
    };

    return payload;
  }

  isNullOrEmptyString(str?: string | null) {
    return str === '' || str === null || str === undefined;
  }

  formatDate(dateString: string, time = false) {
    const date = new Date(dateString);
    if (time) {
      const date = new Date(dateString);

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  }

  formatDateTo(dateString: string, format: string) {
    if (!dateString) return dateString;
    const date = new Date(dateString);
    return this.datePipe.transform(date, format) ?? '';
  }

  formatNumberTo(
    number: number | string | null | undefined,
    digitsInfo: string = '1.0-2'
  ) {
    return number ? this.decimalPipe.transform(number, digitsInfo) : number;
  }

  /**
   * Handles redirection to PMD with the provided data and settings
   * @param dialogData The dialog data containing rowData and siLinkingMode
   * @param company Company information
   * @param plantationsBySi$ Observable of plantations (optional)
   * @param viewAsSetting$ Observable of view settings
   */
  handlePmdRedirection(
    dialogData: TraceabilityData & {
      rowData?: TraceabilityData | null;
      siLinkingMode?: string;
    },
    company: { type?: string | null; name?: string; mode?: string },
    viewAsSetting$?: Observable<UserViewAsSetting | null>
  ) {
    const rowData =
      ('rowData' in dialogData ? dialogData.rowData : dialogData) ?? dialogData;
    const siLinkingMode =
      ('siLinkingMode' in dialogData
        ? dialogData.siLinkingMode
        : company.mode) ?? company.mode;

    rowData.company = {
      ...company,
      linkingMode: siLinkingMode
    };

    if (viewAsSetting$) {
      const viewSettings = this.retrieveFromObservable(viewAsSetting$);
      if (viewSettings?.view !== null && viewSettings?.view !== undefined) {
        rowData.defaultMode = viewSettings.view.toString();
      }
    }

    this.emitPropToParent(
      'sidetails',
      rowData,
      'type',
      PostMessageTypesEnum.REDIRECT_PMD
    );
  }

  parseIntOrNull(str: string | null) {
    if (str === null || str === '') {
      return null;
    }

    const parsed = Number.parseInt(str);
    return Number.isNaN(parsed) ? null : parsed;
  }

  getRiskByName(value: string) {
    return (
      getRiskTypeList().find((risk) => risk.name === value)?.display_name ?? ''
    );
  }

  removeEmptyProperties<T extends Record<string, unknown>>(obj: T): Partial<T> {
    Object.keys(obj).forEach((key) => {
      if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
        delete obj[key];
      }
    });

    return obj;
  }

  getLang(lang: string): string {
    const langMap: Record<string, string> = {
      cn: 'zh-CN',
      id: 'id-ID',
      th: 'th-TH'
    };
    return langMap[lang] ?? lang;
  }

  getUniqueBy(
    arrayOfObjects: TraceabilityData[] | null | undefined,
    keyObject: 'riskObjects',
    key: 'key'
  ) {
    return Array.from(
      new Set(
        arrayOfObjects?.flatMap(
          (a) => a[keyObject]?.map((a) => a[key]) ?? []
        ) ?? []
      )
    );
  }
}
